/**
 * PlantBox Calculator Section
 *
 * Calculates how many plantboxes (60.5cm × 20.2cm) fit into a user-defined area.
 * Uses a grid fitting approach:
 *   horizontal = floor(width / 60.5)
 *   vertical = floor(height / 20.2)
 *   total = horizontal × vertical
 *
 * Recommends set combinations using greedy algorithm: 10 → 5 → 3 → 1
 * Renders SVG visualization showing the outer area and fitted plantboxes.
 */

class PlantboxCalculatorSection extends HTMLElement {
  constructor() {
    super();
    this.sets = [10, 5, 3, 1];
  }

  connectedCallback() {
    // Plantbox dimensions in cm — fractional (605 × 202 mm), so parse as float
    this.plantboxW = parseFloat(this.dataset.plantboxWidth) || 60.5;
    this.plantboxH = parseFloat(this.dataset.plantboxHeight) || 20.2;

    // Recommended reserve space above PlantBoxes (cm)
    this.reserveHeight = parseInt(this.dataset.reserveHeight) || 20;

    // B2B / tax-exempt flag for "bez DPH" total calculation
    this.customerTaxExempt = this.dataset.customerTaxExempt === 'true';

    // Size tier thresholds for dynamic photos
    this.mediumThreshold = parseInt(this.dataset.mediumThreshold) || 6;
    this.largeThreshold = parseInt(this.dataset.largeThreshold) || 16;
    this.currentTier = null;

    // AR models — exact match only (array of {h, w, url})
    try {
      this.arModels = JSON.parse(this.dataset.arModels || '[]');
    } catch (e) {
      this.arModels = [];
    }
    this.currentArUrl = null;

    // Variant data — each size is now its own product
    this.variants = {};
    for (const size of this.sets) {
      const key = size === 1 ? 'single' : `set${size}`;
      const id = this.dataset[`${key}VariantId`];
      if (id) {
        const price = parseInt(this.dataset[`${key}VariantPrice`]) || 0;
        const originalPrice = parseInt(this.dataset[`${key}VariantOriginalPrice`]) || price;
        const compareAtPrice = parseInt(this.dataset[`${key}VariantCompareAtPrice`]) || 0;
        this.variants[size] = {
          id: id,
          price: price,
          originalPrice: originalPrice,
          compareAtPrice: compareAtPrice,
          isDiscounted: price < originalPrice,
          title: this.dataset[`${key}VariantTitle`] || `Set of ${size}`,
          productHandle: this.dataset[`${key}ProductHandle`] || '',
          productId: this.dataset[`${key}ProductId`] || '',
          collectionIds: this.dataset[`${key}CollectionIds`] || '',
        };
      }
    }

    // Plug (zátka) variant
    this.plugVariant = null;
    const plugId = this.dataset.plugVariantId;
    if (plugId) {
      const plugPrice = parseInt(this.dataset.plugVariantPrice) || 0;
      const plugOriginalPrice = parseInt(this.dataset.plugVariantOriginalPrice) || plugPrice;
      this.plugVariant = {
        id: plugId,
        price: plugPrice,
        originalPrice: plugOriginalPrice,
        compareAtPrice: parseInt(this.dataset.plugVariantCompareAtPrice) || 0,
        isDiscounted: plugPrice < plugOriginalPrice,
        title: this.dataset.plugVariantTitle || 'Plug',
        productHandle: this.dataset.plugProductHandle || '',
        productId: this.dataset.plugProductId || '',
        collectionIds: this.dataset.plugCollectionIds || '',
      };
    }

    // Závlaha (irrigation) — optional add-on with 6 variants
    this.zavlahaVariants = [];
    this.zavlahaEnabled = false;
    this.zavlahaUnits = null;
    try {
      this.zavlahaVariants = JSON.parse(this.dataset.zavlahaVariants || '[]');
    } catch (e) {
      this.zavlahaVariants = [];
    }

    this.loadTranslations();
    this.setupElements();
    this.setupListeners();

    // Auto-calculate on load if default values are present
    if (this.widthInput?.value && this.heightInput?.value) {
      this.calculate();
    }
  }

  loadTranslations() {
    this.t = {
      plantboxes: this.dataset.tPlantboxes || 'PlantBoxů',
      sideBySide: this.dataset.tSideBySide || 'vedle sebe',
      stacked: this.dataset.tStacked || 'na sobě',
      plugsPerColumn: this.dataset.tPlugsPerColumn || 'ks (1 na každý sloupec)',
      tooSmallTitle: this.dataset.tTooSmallTitle || 'Zadaná plocha je příliš malá.',
      tooSmallMin: this.dataset.tTooSmallMin || 'Minimální rozměr je',
      tooSmallForOne: this.dataset.tTooSmallForOne || 'cm pro jeden PlantBox.',
      perfectHeading: this.dataset.tPerfectHeading || 'Perfektní!',
      perfectBody: this.dataset.tPerfectBody || 'PlantBoxy přesně pokryjí celou zadanou plochu.',
      coveragePrefix: this.dataset.tCoveragePrefix || 'PlantBoxy pokryjí',
      remainingPrefix: this.dataset.tRemainingPrefix || 'Zbývá',
      unusedSpace: this.dataset.tUnusedSpace || 'nevyužitého prostoru.',
      widthSuffix: this.dataset.tWidthSuffix || 'cm na šířku',
      heightSuffix: this.dataset.tHeightSuffix || 'cm na výšku',
      andSeparator: this.dataset.tAndSeparator || 'a',
      addingToCart: this.dataset.tAddingToCart || 'Přidávám...',
      addedToCart: this.dataset.tAddedToCart || 'Přidáno!',
      perPiece: this.dataset.tPerPiece || '/ ks',
      reserveInfo: this.dataset.tReserveInfo || 'Zahrnuje doporučený prostor 20 cm nad PlantBoxy.',
      zavlahaTitle: this.dataset.tZavlahaTitle || 'Venkovní závlaha',
      zavlahaToggle: this.dataset.tZavlahaToggle || 'Přidat venkovní závlahu',
      zavlahaUnavailable: this.dataset.tZavlahaUnavailable || 'Pro tuto konfiguraci není závlaha dostupná.',
      zavlahaSoldOut: this.dataset.tZavlahaSoldOut || 'Doporučená varianta závlahy je vyprodaná.',
      zavlahaLegend: this.dataset.tZavlahaLegend || 'Závlaha',
      accessoriesTitle: this.dataset.tAccessoriesTitle || 'Doplňky',
    };
  }

  setupElements() {
    this.widthInput = this.querySelector('[data-calc-width]');
    this.heightInput = this.querySelector('[data-calc-height]');
    this.calcBtn = this.querySelector('[data-calc-button]');
    this.resultsEl = this.querySelector('[data-calc-results]');
    this.areaDisplay = this.querySelector('[data-result-area]');
    this.coverageDisplay = this.querySelector('[data-result-coverage]');
    this.totalDisplay = this.querySelector('[data-result-total]');
    this.gridDisplay = this.querySelector('[data-result-grid]');
    this.plugRow = this.querySelector('[data-result-plug-row]');
    this.plugDisplay = this.querySelector('[data-result-plugs]');
    this.messageEl = this.querySelector('[data-calc-message]');
    this.recoList = this.querySelector('[data-reco-list]');
    this.recoTotalPrice = this.querySelector('[data-reco-total-price]');
    this.addToCartBtn = this.querySelector('[data-calc-add-to-cart]');
    this.svgEl = this.querySelector('[data-calc-svg]');
    this.visualEl = this.querySelector('[data-calc-visual]');
    this.placeholderEl = this.querySelector('.plantbox-calc__visual-placeholder');
    this.legendEl = this.querySelector('[data-calc-legend]');

    // Závlaha toggle
    this.accessoriesEl = this.querySelector('[data-accessories]');
    this.zavlahaToggleEl = this.querySelector('[data-zavlaha-toggle]');
    this.zavlahaRowEl = this.querySelector('[data-zavlaha-row]');
    this.zavlahaVariantTitleEl = this.querySelector('[data-zavlaha-variant-title]');
    this.zavlahaPriceEl = this.querySelector('[data-zavlaha-price]');
    this.zavlahaTooltipEl = this.querySelector('[data-zavlaha-tooltip]');
    this.zavlahaLegendItems = this.querySelectorAll('[data-zavlaha-legend-item]');

    // View switcher
    this.viewBtns = this.querySelectorAll('[data-view-btn]');
    this.viewPanels = this.querySelectorAll('[data-view-panel]');

  }

  setupListeners() {
    this.calcBtn?.addEventListener('click', () => this.calculate());

    // Enter key triggers calculation
    [this.widthInput, this.heightInput].forEach((input) => {
      input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.calculate();
        }
      });
    });

    this.addToCartBtn?.addEventListener('click', (e) => this.addToCart(e));

    // Závlaha toggle
    this.zavlahaToggleEl?.addEventListener('change', () => {
      this.zavlahaEnabled = this.zavlahaToggleEl.checked;
      this.onZavlahaChange();
    });

    // View switcher
    this.viewBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.switchView(btn.dataset.viewBtn));
    });

    // Photo thumbnails — show photo, hide AR viewer
    this.querySelectorAll('[data-photo-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const container = thumb.closest('.plantbox-calc__tier-group') || thumb.closest('.plantbox-calc__view-panel');
        const mainImg = container?.querySelector('[data-photo-main]');
        if (!mainImg) return;
        // If this is the AR thumb, show 3D viewer instead
        if (thumb.hasAttribute('data-ar-thumb')) {
          mainImg.style.display = 'none';
          const arWrap = container.querySelector('[data-ar-viewer-wrap]');
          if (arWrap) arWrap.style.display = '';
          container.querySelectorAll('[data-photo-thumb]').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          this.loadModelViewer();
          return;
        }
        // Regular photo thumb — show photo, hide AR
        mainImg.style.display = '';
        mainImg.src = thumb.dataset.full;
        mainImg.alt = thumb.alt;
        const arWrap = container.querySelector('[data-ar-viewer-wrap]');
        if (arWrap) arWrap.style.display = 'none';
        container.querySelectorAll('[data-photo-thumb]').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    // Lightbox on main photo click
    this.querySelectorAll('[data-photo-main]').forEach((img) => {
      img.addEventListener('click', () => this.openLightbox(img.src, img.alt));
    });
  }

  openLightbox(src, alt) {
    const overlay = document.createElement('div');
    overlay.className = 'plantbox-calc__lightbox';
    overlay.innerHTML = `
      <button class="plantbox-calc__lightbox-close" aria-label="Close">&times;</button>
      <img src="${src}" alt="${alt || ''}">
    `;
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('.plantbox-calc__lightbox-close')) {
        overlay.remove();
      }
    });
    document.addEventListener('keydown', function handler(e) {
      if (e.key === 'Escape') {
        overlay.remove();
        document.removeEventListener('keydown', handler);
      }
    });
    document.body.appendChild(overlay);
  }

  switchView(viewId) {
    this.viewBtns.forEach((btn) => {
      const isActive = btn.dataset.viewBtn === viewId;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });
    this.viewPanels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.viewPanel === viewId);
    });
  }

  loadModelViewer() {
    if (this._modelViewerLoaded) return;
    this._modelViewerLoaded = true;
    const s = document.createElement('script');
    s.type = 'module';
    s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
    document.head.appendChild(s);
  }

  updateSizeTier(totalPots) {
    let tier;
    if (totalPots >= this.largeThreshold) {
      tier = 'large';
    } else if (totalPots >= this.mediumThreshold) {
      tier = 'medium';
    } else {
      tier = 'small';
    }

    if (tier !== this.currentTier) {
      this.currentTier = tier;
      this.querySelectorAll('.plantbox-calc__tier-group').forEach((group) => {
        group.classList.toggle('is-active', group.dataset.sizeTier === tier);
      });
    }
  }

  updateArModel(rows, cols) {
    if (!this.arModels.length) return;

    // Find exact match: h = rows (height/stacked), w = cols (width/side-by-side)
    const match = this.arModels.find((m) => m.h === rows && m.w === cols);

    // Update all tier groups (the active one will be visible)
    this.querySelectorAll('.plantbox-calc__tier-group').forEach((group) => {
      const arThumb = group.querySelector('[data-ar-thumb]');
      const arWrap = group.querySelector('[data-ar-viewer-wrap]');
      const arViewer = group.querySelector('[data-ar-viewer]');
      const arCaption = group.querySelector('[data-ar-caption]');

      if (match && arThumb) {
        // Show AR thumbnail
        arThumb.style.display = '';

        // Set model URL on thumbnail viewer
        const thumbViewer = group.querySelector('[data-ar-thumb-viewer]');
        if (thumbViewer && thumbViewer.getAttribute('src') !== match.url) {
          thumbViewer.setAttribute('src', match.url);
          this.loadModelViewer();
        }

        // Set model URL on main viewer
        if (arViewer) {
          arViewer.setAttribute('src', match.url);
          arViewer.setAttribute('alt', `PlantBox ${rows}×${cols} 3D model`);
        }
        if (arCaption) {
          const captionTemplate = this.dataset.tArExactCaption || 'AR model: {h}×{w} ({h} stacked, {w} side by side)';
          arCaption.textContent = captionTemplate.replaceAll('{h}', rows).replaceAll('{w}', cols);
        }
      } else {
        // No match — hide AR thumb and viewer, show photo
        if (arThumb) {
          arThumb.style.display = 'none';
          arThumb.classList.remove('is-active');
        }
        if (arWrap) arWrap.style.display = 'none';
        const mainImg = group.querySelector('[data-photo-main]');
        if (mainImg) mainImg.style.display = '';
      }
    });

    this.currentArUrl = match ? match.url : null;
  }

  /**
   * Plantbox dimensions are fractional (60.5 / 20.2 cm), so their multiples carry
   * float noise (3 × 20.2 = 60.599999999999994). Round to one decimal for display
   * and comparisons; whole numbers keep no trailing ".0".
   */
  round1(value) {
    return Math.round(value * 10) / 10;
  }

  calculate() {
    const width = parseInt(this.widthInput?.value) || 0;
    const height = parseInt(this.heightInput?.value) || 0;

    if (width < 1 || height < 1) return;

    // Subtract mandatory reserve from available height
    const availableHeight = height - this.reserveHeight;

    // Grid fitting
    const cols = Math.floor(width / this.plantboxW);
    const rows = availableHeight > 0 ? Math.floor(availableHeight / this.plantboxH) : 0;
    const totalPots = cols * rows;

    if (totalPots === 0) {
      this.showNoFitMessage(width, height);
      return;
    }

    // Coverage
    const coverageW = cols * this.plantboxW;
    const coverageH = rows * this.plantboxH;
    const userArea = width * height;
    const coverageArea = coverageW * coverageH;

    // Recommend sets
    this.recommendation = this.calculateSets(totalPots);
    this.recommendation.cols = cols;
    this.recommendation.availableHeight = availableHeight;
    this.recommendation.rows = rows;

    // Update displays
    this.areaDisplay.textContent = `${width} × ${height} cm (${(userArea / 10000).toFixed(2)} m²)`;
    this.coverageDisplay.textContent = `${this.round1(coverageW)} × ${this.round1(coverageH)} cm (${(coverageArea / 10000).toFixed(2)} m²)`;
    this.totalDisplay.textContent = `${totalPots} ${this.t.plantboxes}`;
    this.gridDisplay.textContent = `${cols} × ${rows} (${cols} ${this.t.sideBySide}, ${rows} ${this.t.stacked})`;

    if (this.plugVariant && this.plugDisplay && this.plugRow) {
      this.plugDisplay.textContent = `${cols} ${this.t.plugsPerColumn}`;
      this.plugRow.style.display = '';
    }

    // Message about extra/unused space
    this.updateMessage(width, height, coverageW, coverageH);

    // Render recommendations
    this.renderRecommendations(this.recommendation);

    // Závlaha add-on (optional, depends on cols + available height)
    this.updateZavlaha(cols, availableHeight);

    // Show results
    this.resultsEl.style.display = '';

    // Draw visualization
    this.drawVisualization(width, height, cols, rows);

    // Switch photos to matching size tier
    this.updateSizeTier(totalPots);

    // Show AR only if exact model match exists
    this.updateArModel(rows, cols);
  }

  showNoFitMessage(width, height) {
    this.resultsEl.style.display = '';
    this.areaDisplay.textContent = `${width} × ${height} cm`;
    this.coverageDisplay.textContent = '0 × 0 cm';
    this.totalDisplay.textContent = '0';
    this.gridDisplay.textContent = '-';

    this.messageEl.className = 'plantbox-calc__message plantbox-calc__message--short';
    const minH = this.round1(this.plantboxH + this.reserveHeight);
    this.messageEl.innerHTML = `<strong>${this.t.tooSmallTitle}</strong> ${this.t.tooSmallMin} ${this.round1(this.plantboxW)} × ${minH} ${this.t.tooSmallForOne}`;
    this.messageEl.style.display = '';

    this.recoList.innerHTML = '';
    this.recoTotalPrice.textContent = '-';

    // Hide visual
    if (this.svgEl) this.svgEl.style.display = 'none';
    if (this.placeholderEl) this.placeholderEl.style.display = '';
    if (this.legendEl) this.legendEl.style.display = 'none';
  }

  calculateSets(totalPots) {
    let remaining = totalPots;
    const result = {};
    const availableSets = this.sets.filter((s) => this.variants[s]);

    for (const setSize of availableSets) {
      if (remaining >= setSize) {
        result[setSize] = Math.floor(remaining / setSize);
        remaining = remaining % setSize;
      }
    }

    // If remaining and we have singles
    if (remaining > 0 && this.variants[1]) {
      result[1] = (result[1] || 0) + remaining;
      remaining = 0;
    }

    // If still remaining, add one more of smallest available
    if (remaining > 0) {
      const smallest = availableSets[availableSets.length - 1];
      if (smallest) {
        result[smallest] = (result[smallest] || 0) + 1;
      }
    }

    // Calculate total provided
    let totalProvided = 0;
    for (const [size, qty] of Object.entries(result)) {
      totalProvided += parseInt(size) * qty;
    }

    return { sets: result, totalPots, totalProvided };
  }

  updateMessage(userW, userH, covW, covH) {
    const extraW = this.round1(userW - covW);
    const extraH = this.round1(userH - covH - this.reserveHeight);
    const reserveNote = `<div style="color:#C47A5A;margin-top:0.4em;">${this.t.reserveInfo}</div>`;

    if (extraW === 0 && extraH <= 0) {
      this.messageEl.className = 'plantbox-calc__message plantbox-calc__message--perfect';
      this.messageEl.innerHTML = `<strong>${this.t.perfectHeading}</strong> ${this.t.perfectBody}${reserveNote}`;
    } else {
      const parts = [];
      if (extraW > 0) parts.push(`${extraW} ${this.t.widthSuffix}`);
      if (extraH > 0) parts.push(`${extraH} ${this.t.heightSuffix}`);
      if (parts.length === 0) {
        this.messageEl.className = 'plantbox-calc__message plantbox-calc__message--perfect';
        this.messageEl.innerHTML = `<strong>${this.t.perfectHeading}</strong> ${this.t.perfectBody}${reserveNote}`;
      } else {
        this.messageEl.className = 'plantbox-calc__message plantbox-calc__message--extra';
        this.messageEl.innerHTML = `${this.t.coveragePrefix} <strong>${this.round1(covW)} × ${this.round1(covH)} cm</strong>. ${this.t.remainingPrefix} ${parts.join(` ${this.t.andSeparator} `)} ${this.t.unusedSpace}${reserveNote}`;
      }
    }
    this.messageEl.style.display = '';
  }

  renderRecommendations(reco) {
    this.lastReco = reco;
    this.recoList.innerHTML = '';
    let totalPrice = 0;
    let totalOriginalPrice = 0;
    this.cartItems = [];

    for (const [size, qty] of Object.entries(reco.sets)) {
      if (qty <= 0) continue;
      const variant = this.variants[parseInt(size)];
      if (!variant) continue;

      const itemPrice = variant.price * qty;
      const itemOriginalPrice = variant.originalPrice * qty;
      totalPrice += itemPrice;
      totalOriginalPrice += itemOriginalPrice;

      this.cartItems.push({ id: parseInt(variant.id), quantity: qty });

      // Build price display with optional strikethrough for B2B discounts
      let priceHtml = '';
      if (variant.isDiscounted) {
        priceHtml = `<s class="plantbox-calc__reco-item-original-price">${this.formatMoney(variant.originalPrice)}</s> <span class="plantbox-calc__reco-item-discount-price" data-wpd-product-handle="${variant.productHandle}" data-wpd-variant-id="${variant.id}" data-wpd-variant-price="${variant.originalPrice}" data-wpd-variant-compare-at-price="${variant.compareAtPrice}" data-wpd-product-collection-ids="${variant.collectionIds}" data-wpd-product-id="${variant.productId}">${this.formatMoney(variant.price)}</span> ${this.t.perPiece}`;
      } else {
        priceHtml = `<span data-wpd-product-handle="${variant.productHandle}" data-wpd-variant-id="${variant.id}" data-wpd-variant-price="${variant.originalPrice}" data-wpd-variant-compare-at-price="${variant.compareAtPrice}" data-wpd-product-collection-ids="${variant.collectionIds}" data-wpd-product-id="${variant.productId}">${this.formatMoney(variant.price)}</span> ${this.t.perPiece}`;
      }

      const li = document.createElement('li');
      li.className = 'plantbox-calc__reco-item';
      li.innerHTML = `
        <div class="plantbox-calc__reco-item-info">
          <span class="plantbox-calc__reco-item-name">${variant.title}</span>
          <span class="plantbox-calc__reco-item-price">${priceHtml}</span>
        </div>
        <div class="plantbox-calc__reco-item-qty">
          <span class="plantbox-calc__reco-item-qty-label">×</span>
          <span class="plantbox-calc__reco-item-qty-value">${qty}</span>
        </div>
      `;
      this.recoList.appendChild(li);
    }

    // Add plug (zátka) - one per column
    if (this.plugVariant && reco.cols > 0) {
      const pv = this.plugVariant;
      const plugQty = reco.cols;
      const plugPrice = pv.price * plugQty;
      const plugOriginalPrice = pv.originalPrice * plugQty;
      totalPrice += plugPrice;
      totalOriginalPrice += plugOriginalPrice;

      this.cartItems.push({ id: parseInt(pv.id), quantity: plugQty });

      let plugPriceHtml = '';
      if (pv.isDiscounted) {
        plugPriceHtml = `<s class="plantbox-calc__reco-item-original-price">${this.formatMoney(pv.originalPrice)}</s> <span class="plantbox-calc__reco-item-discount-price" data-wpd-product-handle="${pv.productHandle}" data-wpd-variant-id="${pv.id}" data-wpd-variant-price="${pv.originalPrice}" data-wpd-variant-compare-at-price="${pv.compareAtPrice}" data-wpd-product-collection-ids="${pv.collectionIds}" data-wpd-product-id="${pv.productId}">${this.formatMoney(pv.price)}</span> ${this.t.perPiece}`;
      } else {
        plugPriceHtml = `<span data-wpd-product-handle="${pv.productHandle}" data-wpd-variant-id="${pv.id}" data-wpd-variant-price="${pv.originalPrice}" data-wpd-variant-compare-at-price="${pv.compareAtPrice}" data-wpd-product-collection-ids="${pv.collectionIds}" data-wpd-product-id="${pv.productId}">${this.formatMoney(pv.price)}</span> ${this.t.perPiece}`;
      }

      const plugLi = document.createElement('li');
      plugLi.className = 'plantbox-calc__reco-item plantbox-calc__reco-item--plug';
      plugLi.innerHTML = `
        <div class="plantbox-calc__reco-item-info">
          <span class="plantbox-calc__reco-item-name">${pv.title}</span>
          <span class="plantbox-calc__reco-item-price">${plugPriceHtml}</span>
        </div>
        <div class="plantbox-calc__reco-item-qty">
          <span class="plantbox-calc__reco-item-qty-label">×</span>
          <span class="plantbox-calc__reco-item-qty-value">${plugQty}</span>
        </div>
      `;
      this.recoList.appendChild(plugLi);
    }

    // Show initial total from Liquid data attributes
    if (totalPrice < totalOriginalPrice) {
      this.recoTotalPrice.innerHTML = `<s class="plantbox-calc__reco-total-original">${this.formatMoney(totalOriginalPrice)}</s> ${this.formatMoney(totalPrice)}`;
    } else {
      this.recoTotalPrice.textContent = this.formatMoney(totalPrice);
    }

    // Watch for WPD JS to modify individual price elements, then sum them
    this.observeWpdPriceChanges();
  }

  observeWpdPriceChanges() {
    if (this._wpdObserver) this._wpdObserver.disconnect();

    let debounce = null;
    this._wpdObserver = new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(() => this.recalcTotalFromDisplayedPrices(), 300);
    });

    this._wpdObserver.observe(this.recoList, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Also poll — WPD may have already modified elements before the
    // observer was registered, or may use a mechanism (setInterval)
    // that doesn't trigger MutationObserver reliably.
    [500, 1000, 2000, 3000, 5000].forEach(delay => {
      setTimeout(() => this.recalcTotalFromDisplayedPrices(), delay);
    });
  }

  recalcTotalFromDisplayedPrices() {
    let wpdTotal = 0;
    let originalTotal = 0;
    let hasWpdChange = false;

    const items = this.recoList.querySelectorAll('.plantbox-calc__reco-item');
    for (const item of items) {
      const qtyEl = item.querySelector('.plantbox-calc__reco-item-qty-value');
      const qty = parseInt(qtyEl?.textContent) || 0;
      if (qty <= 0) continue;

      const wpdEl = item.querySelector('[data-wpd-variant-id]');
      if (!wpdEl) continue;

      const origCents = parseInt(wpdEl.dataset.wpdVariantPrice) || 0;
      const displayedCents = this.extractWpdPrice(wpdEl);

      if (displayedCents > 0 && displayedCents !== origCents) {
        hasWpdChange = true;
        wpdTotal += displayedCents * qty;
      } else {
        wpdTotal += origCents * qty;
      }

      if (this.customerTaxExempt) {
        originalTotal += Math.round(origCents / 1.21) * qty;
      } else {
        originalTotal += origCents * qty;
      }
    }

    if (hasWpdChange && wpdTotal < originalTotal) {
      this.recoTotalPrice.innerHTML =
        `<s class="plantbox-calc__reco-total-original">${this.formatMoney(originalTotal)}</s> ${this.formatMoney(wpdTotal)}`;
    }
  }

  /**
   * WPD replaces element content with HTML like:
   *   <s style="color:red">726,00 Kč</s> 510,00 Kč bez DPH<br>...
   * Clone the element, remove <s>/<del>/<strike> (original price),
   * then extract the first price from the remaining text (discounted price).
   */
  extractWpdPrice(el) {
    const clone = el.cloneNode(true);
    clone.querySelectorAll('s, del, strike').forEach(node => node.remove());
    return this.extractPriceCents(clone.textContent);
  }

  extractPriceCents(text) {
    const m = text.match(/([\d.]+,\d{2})/);
    if (!m) return 0;
    const num = m[1].replace(/\./g, '').replace(',', '.');
    return Math.round(parseFloat(num) * 100);
  }

  /**
   * Find závlaha variants matching the current grid. The default unit (≤3
   * modules) sits on the right; when cols > 3 an overflow unit appears on the
   * left. Returns:
   *   { units: [{variant, side, colCount, startCol}], heightBucket }
   * or null when cols ∉ [1,6] or height > 250cm.
   */
  findZavlahaVariants(cols, availableHeightCm) {
    if (!this.zavlahaVariants.length) return null;
    if (cols < 1 || cols > 6) return null;
    if (availableHeightCm > 250) return null;
    const heightBucket = availableHeightCm <= 120 ? '1,2' : '2,5';

    const rightCount = Math.min(3, cols);
    const leftCount = Math.max(0, cols - 3);
    const layout = leftCount > 0
      ? [{ count: leftCount, side: 'left' }, { count: rightCount, side: 'right' }]
      : [{ count: rightCount, side: 'right' }];

    const units = [];
    let startCol = 0;
    for (const { count, side } of layout) {
      const colWord = count === 1 ? 'modul' : 'moduly';
      const needle = `${count} ${colWord} · výška do ${heightBucket} m`;
      const variant = this.zavlahaVariants.find((v) => v.title === needle) || null;
      units.push({
        variant,
        side,
        colCount: count,
        startCol,
      });
      startCol += count;
    }
    return { units, heightBucket };
  }

  /**
   * Build the compact combined title from unit variants sharing a height bucket,
   * e.g. "3 moduly + 1 modul · výška do 1,2 m". Falls back to joining raw titles
   * with " + " if any unit variant is missing.
   */
  buildZavlahaCombinedTitle(units, heightBucket) {
    const suffix = ` · výška do ${heightBucket} m`;
    const parts = units.map((u) => {
      if (!u.variant) return `${u.colCount} ${u.colCount === 1 ? 'modul' : 'moduly'}`;
      return u.variant.title.split(' · ')[0];
    });
    return parts.join(' + ') + suffix;
  }

  updateZavlaha(cols, availableHeightCm) {
    if (!this.accessoriesEl || !this.zavlahaVariants.length) return;

    this.accessoriesEl.style.display = '';
    const group = this.findZavlahaVariants(cols, availableHeightCm);

    if (!group) {
      this.zavlahaUnits = null;
      this.zavlahaRowEl.classList.add('is-disabled');
      this.zavlahaRowEl.dataset.tooltip = this.t.zavlahaUnavailable;
      this.zavlahaToggleEl.disabled = true;
      this.zavlahaToggleEl.checked = false;
      this.zavlahaEnabled = false;
      this.zavlahaVariantTitleEl.textContent = '';
      this.zavlahaPriceEl.textContent = '';
      this.zavlahaTooltipEl.textContent = this.t.zavlahaUnavailable;
      this.zavlahaTooltipEl.style.display = '';
      this.applyZavlahaToRecoAndTotal();
      return;
    }

    const allAvailable = group.units.every((u) => u.variant && u.variant.available !== false);
    const combinedTitle = this.buildZavlahaCombinedTitle(group.units, group.heightBucket);

    if (!allAvailable) {
      this.zavlahaUnits = null;
      this.zavlahaRowEl.classList.add('is-disabled');
      this.zavlahaRowEl.dataset.tooltip = this.t.zavlahaSoldOut;
      this.zavlahaToggleEl.disabled = true;
      this.zavlahaToggleEl.checked = false;
      this.zavlahaEnabled = false;
      this.zavlahaVariantTitleEl.textContent = combinedTitle;
      this.zavlahaPriceEl.textContent = '';
      this.zavlahaTooltipEl.textContent = this.t.zavlahaSoldOut;
      this.zavlahaTooltipEl.style.display = '';
      this.applyZavlahaToRecoAndTotal();
      return;
    }

    this.zavlahaUnits = group.units;
    const totalPrice = group.units.reduce((sum, u) => sum + u.variant.price, 0);
    this.zavlahaRowEl.classList.remove('is-disabled');
    this.zavlahaToggleEl.disabled = false;
    this.zavlahaVariantTitleEl.textContent = combinedTitle;
    this.zavlahaPriceEl.textContent = `+ ${this.formatMoney(totalPrice)}`;
    this.zavlahaTooltipEl.textContent = '';
    this.zavlahaTooltipEl.style.display = 'none';
    this.applyZavlahaToRecoAndTotal();
  }

  /**
   * Called when závlaha toggle changes state OR when eligibility changes.
   * Syncs cartItems and re-computes total (WPD re-sync is handled via existing observer).
   */
  onZavlahaChange() {
    this.applyZavlahaToRecoAndTotal();
    // Re-draw SVG so the blue irrigation overlay appears/disappears
    if (this.lastReco) {
      const w = parseInt(this.widthInput?.value) || 0;
      const h = parseInt(this.heightInput?.value) || 0;
      if (w > 0 && h > 0) {
        this.drawVisualization(w, h, this.lastReco.cols, this.lastReco.rows);
      }
    }
  }

  applyZavlahaToRecoAndTotal() {
    if (!this.cartItems) return;

    // Remove any previously-added závlaha cart items
    const zavlahaIds = new Set(this.zavlahaVariants.map((v) => parseInt(v.id)));
    this.cartItems = this.cartItems.filter((item) => !zavlahaIds.has(item.id));

    // Add every recommended unit if the toggle is on
    const unitsActive = this.zavlahaEnabled && this.zavlahaUnits?.length;
    if (unitsActive) {
      for (const unit of this.zavlahaUnits) {
        this.cartItems.push({ id: parseInt(unit.variant.id), quantity: 1 });
      }
    }

    // Recompute displayed total from current recoList + závlaha
    let total = 0;
    const items = this.recoList.querySelectorAll('.plantbox-calc__reco-item');
    for (const item of items) {
      const qtyEl = item.querySelector('.plantbox-calc__reco-item-qty-value');
      const qty = parseInt(qtyEl?.textContent) || 0;
      const wpdEl = item.querySelector('[data-wpd-variant-id]');
      if (!wpdEl || qty <= 0) continue;
      const origCents = parseInt(wpdEl.dataset.wpdVariantPrice) || 0;
      total += origCents * qty;
    }
    if (unitsActive) {
      for (const unit of this.zavlahaUnits) {
        total += unit.variant.price;
      }
    }
    if (total > 0) {
      this.recoTotalPrice.textContent = this.formatMoney(total);
    }
  }

  drawVisualization(userW, userH, cols, rows) {
    if (!this.svgEl) return;

    const padding = 40;
    const dimSpaceLeft = 50;
    const dimSpaceRight = 60;
    const dimSpaceTop = 24;
    const dimSpaceBottom = 30;
    const svgWidth = 420;
    const svgHeight = 370;
    const drawAreaW = svgWidth - padding * 2 - dimSpaceLeft - dimSpaceRight;
    const drawAreaH = svgHeight - padding * 2 - dimSpaceTop - dimSpaceBottom;

    // Scale factor to fit user dimensions into draw area
    const scale = Math.min(drawAreaW / userW, drawAreaH / userH);
    const scaledW = userW * scale;
    const scaledH = userH * scale;

    // Center offset
    const ox = padding + dimSpaceLeft + (drawAreaW - scaledW) / 2;
    const oy = padding + dimSpaceTop + (drawAreaH - scaledH) / 2;

    // Coverage area
    const covW = cols * this.plantboxW * scale;
    const covH = rows * this.plantboxH * scale;

    // Plantboxes sit on the ground (bottom-aligned)
    const covTopY = oy + scaledH - covH;

    // Reserve zone above PlantBoxes
    const reserveScaled = this.reserveHeight * scale;
    const reserveTop = Math.max(covTopY - reserveScaled, oy);
    const reserveH = covTopY - reserveTop;

    // Build SVG content
    let svg = '';

    // Hatch pattern definition for reserve zone
    const hatchId = `hatch-${Date.now()}`;
    svg += `<defs>
      <pattern id="${hatchId}" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="#C47A5A" stroke-width="1.2" opacity="0.45"/>
      </pattern>
    </defs>`;

    // Outer dashed rectangle (user's area)
    svg += `<rect class="outer-rect" x="${ox}" y="${oy}" width="${scaledW}" height="${scaledH}" rx="2"/>`;

    // Unused space highlight (right strip)
    if (scaledW - covW > 1) {
      svg += `<rect class="unused-area" x="${ox + covW}" y="${oy}" width="${scaledW - covW}" height="${scaledH}" rx="1"/>`;
    }
    // Unused space highlight (top strip - above plantboxes and reserve)
    const unusedTopH = reserveTop - oy;
    if (unusedTopH > 1) {
      svg += `<rect class="unused-area" x="${ox}" y="${oy}" width="${covW}" height="${unusedTopH}" rx="1"/>`;
    }

    // Reserve zone (hatched area above PlantBoxes — mandatory 20cm)
    if (reserveH > 1) {
      svg += `<rect x="${ox}" y="${reserveTop}" width="${covW}" height="${reserveH}" fill="url(#${hatchId})" rx="1"/>`;
      svg += `<rect x="${ox}" y="${reserveTop}" width="${covW}" height="${reserveH}" fill="none" stroke="#C47A5A" stroke-width="1.2" opacity="0.7" rx="1"/>`;
    }

    // Individual plantbox rectangles (bottom-aligned)
    const pbW = this.plantboxW * scale;
    const pbH = this.plantboxH * scale;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = ox + c * pbW;
        const y = covTopY + r * pbH;
        svg += `<rect class="plantbox-rect" x="${x + 0.5}" y="${y + 0.5}" width="${pbW - 1}" height="${pbH - 1}" rx="1"/>`;
      }
    }

    // Plugs — one per column, shaped like a stopper (cap + body)
    if (this.plugVariant) {
      const s = Math.max(Math.min(pbW * 0.14, pbH * 0.35), 6);
      const capW = s;
      const capH = s * 0.35;
      const bodyW = s * 0.55;
      const bodyH = s * 0.65;
      for (let c = 0; c < cols; c++) {
        const cx = ox + c * pbW + s * 0.5 + capW / 2;
        const baseY = covTopY + covH - 2;
        const capX = cx - capW / 2;
        const capY = baseY - capH - bodyH;
        const bodyX = cx - bodyW / 2;
        const bodyY = baseY - bodyH;
        svg += `<rect class="plug-rect plug-body" x="${bodyX}" y="${bodyY}" width="${bodyW}" height="${bodyH}" rx="0.5"/>`;
        svg += `<rect class="plug-rect plug-cap" x="${capX}" y="${capY}" width="${capW}" height="${capH}" rx="0.5"/>`;
      }
    }

    // Závlaha overlay — one independent supply system per recommended unit.
    // Left-side unit feeds from the left edge, right-side unit (when present) from the right edge.
    if (this.zavlahaEnabled && this.zavlahaUnits?.length && cols > 0 && rows > 0) {
      const zBlue = '#3b82f6';
      const reservoirColor = '#C47A5A';
      const manifoldY = reserveH > 10 ? reserveTop + reserveH * 0.75 : covTopY - 6;
      const groundY = oy + scaledH;
      const arrowSize = Math.min(pbH * 0.22, 6);
      const arrowTipY = covTopY + pbH * 0.85;
      const arrowBaseY = arrowTipY - arrowSize * 1.2;

      // Drop arrows sit 15% from the left edge of each plantbox column (0% = right, 100% = left).
      const dropOffset = 0.15;

      for (const unit of this.zavlahaUnits) {
        const firstCx = ox + (unit.startCol + dropOffset) * pbW;
        const lastCx = ox + (unit.startCol + unit.colCount - 1 + dropOffset) * pbW;
        const supplyX = unit.side === 'left' ? ox - 5 : ox + covW + 5;
        const manifoldNearX = unit.side === 'left' ? firstCx : lastCx;

        // Supply riser from manifold down to ground + inlet marker
        svg += `<line x1="${supplyX}" y1="${manifoldY}" x2="${supplyX}" y2="${groundY}" stroke="${zBlue}" stroke-width="1.4" opacity="0.9"/>`;
        svg += `<circle cx="${supplyX}" cy="${groundY}" r="3" fill="${zBlue}"/>`;
        // Top horizontal manifold across this unit's columns
        svg += `<line x1="${supplyX}" y1="${manifoldY}" x2="${manifoldNearX}" y2="${manifoldY}" stroke="${zBlue}" stroke-width="1.4" opacity="0.9"/>`;
        svg += `<line x1="${firstCx}" y1="${manifoldY}" x2="${lastCx}" y2="${manifoldY}" stroke="${zBlue}" stroke-width="1.4" opacity="0.9"/>`;

        for (let c = unit.startCol; c < unit.startCol + unit.colCount; c++) {
          const cx = ox + (c + dropOffset) * pbW;
          svg += `<line x1="${cx}" y1="${manifoldY}" x2="${cx}" y2="${arrowBaseY}" stroke="${zBlue}" stroke-width="1.2" opacity="0.9"/>`;
          svg += `<circle cx="${cx}" cy="${manifoldY}" r="2.5" fill="${zBlue}"/>`;
          svg += `<path d="M ${cx - arrowSize * 0.55} ${arrowBaseY} L ${cx + arrowSize * 0.55} ${arrowBaseY} L ${cx} ${arrowTipY} Z" fill="${zBlue}" opacity="0.95"/>`;
          svg += `<line x1="${cx}" y1="${arrowTipY}" x2="${cx}" y2="${covTopY + covH}" stroke="${zBlue}" stroke-width="1.2" stroke-dasharray="3 2" opacity="0.9"/>`;
        }
      }

      // Per-row red dashed reservoir line spans the entire plantbox coverage — shared across units
      for (let r = 0; r < rows; r++) {
        const reservoirY = covTopY + (r + 0.85) * pbH;
        svg += `<line x1="${ox}" y1="${reservoirY}" x2="${ox + covW}" y2="${reservoirY}" stroke="${reservoirColor}" stroke-width="1" stroke-dasharray="3 2" opacity="0.9"/>`;
      }
    }

    // Coverage outline (bottom-aligned)
    svg += `<rect class="coverage-rect" x="${ox}" y="${covTopY}" width="${covW}" height="${covH}" rx="2"/>`;

    // Dimension labels - Width (top)
    const topY = oy - 14;
    svg += `<line class="dimension-line" x1="${ox}" y1="${topY}" x2="${ox + scaledW}" y2="${topY}"/>`;
    svg += `<line class="dimension-line" x1="${ox}" y1="${topY - 4}" x2="${ox}" y2="${topY + 4}"/>`;
    svg += `<line class="dimension-line" x1="${ox + scaledW}" y1="${topY - 4}" x2="${ox + scaledW}" y2="${topY + 4}"/>`;
    svg += `<text class="dimension-text" x="${ox + scaledW / 2}" y="${topY - 4}" text-anchor="middle">${userW} cm</text>`;

    // Dimension labels - Height (left) — pushed further out so zavlaha supply line has room between it and the outer rect
    const leftX = ox - 22;
    svg += `<line class="dimension-line" x1="${leftX}" y1="${oy}" x2="${leftX}" y2="${oy + scaledH}"/>`;
    svg += `<line class="dimension-line" x1="${leftX - 4}" y1="${oy}" x2="${leftX + 4}" y2="${oy}"/>`;
    svg += `<line class="dimension-line" x1="${leftX - 4}" y1="${oy + scaledH}" x2="${leftX + 4}" y2="${oy + scaledH}"/>`;
    svg += `<text class="dimension-text" x="${leftX - 6}" y="${oy + scaledH / 2}" text-anchor="end" dominant-baseline="middle">${userH} cm</text>`;

    // Coverage dimension lines (PlantBox area) — same style as other kóty
    const pbTotalW = this.round1(cols * this.plantboxW);
    const pbTotalH = this.round1(rows * this.plantboxH);

    // PlantBox width (bottom dimension line)
    const botDimY = oy + scaledH + 14;
    svg += `<line x1="${ox}" y1="${botDimY}" x2="${ox + covW}" y2="${botDimY}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<line x1="${ox}" y1="${botDimY - 4}" x2="${ox}" y2="${botDimY + 4}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<line x1="${ox + covW}" y1="${botDimY - 4}" x2="${ox + covW}" y2="${botDimY + 4}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<text x="${ox + covW / 2}" y="${botDimY + 14}" text-anchor="middle" style="font-size:10px;fill:#5eb130;font-weight:600;">${pbTotalW} cm</text>`;

    // Right-side dimension lines — all at same x, stacked vertically
    const rightDimX = ox + scaledW + 14;

    // Reserve height (brown) — from reserveTop to covTopY
    if (reserveH > 1) {
      svg += `<line x1="${rightDimX}" y1="${reserveTop}" x2="${rightDimX}" y2="${covTopY}" stroke="#C47A5A" stroke-width="1.2"/>`;
      svg += `<line x1="${rightDimX - 4}" y1="${reserveTop}" x2="${rightDimX + 4}" y2="${reserveTop}" stroke="#C47A5A" stroke-width="1.2"/>`;
      svg += `<line x1="${rightDimX - 4}" y1="${covTopY}" x2="${rightDimX + 4}" y2="${covTopY}" stroke="#C47A5A" stroke-width="1.2"/>`;
      svg += `<text x="${rightDimX + 6}" y="${reserveTop + reserveH / 2}" text-anchor="start" dominant-baseline="middle" style="font-size:10px;fill:#C47A5A;font-weight:600;">${this.reserveHeight} cm</text>`;
    }

    // PlantBox height (green) — from covTopY to covTopY + covH
    svg += `<line x1="${rightDimX}" y1="${covTopY}" x2="${rightDimX}" y2="${covTopY + covH}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<line x1="${rightDimX - 4}" y1="${covTopY}" x2="${rightDimX + 4}" y2="${covTopY}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<line x1="${rightDimX - 4}" y1="${covTopY + covH}" x2="${rightDimX + 4}" y2="${covTopY + covH}" stroke="#5eb130" stroke-width="1.2"/>`;
    svg += `<text x="${rightDimX + 6}" y="${covTopY + covH / 2}" text-anchor="start" dominant-baseline="middle" style="font-size:10px;fill:#5eb130;font-weight:600;">${pbTotalH} cm</text>`;

    this.svgEl.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
    this.svgEl.innerHTML = svg;
    this.svgEl.style.display = '';

    // Hide placeholder, show legend
    if (this.placeholderEl) this.placeholderEl.style.display = 'none';
    if (this.legendEl) this.legendEl.style.display = '';

    // Show zavlaha-specific legend items only when zavlaha is active
    const showZavlahaLegend = this.zavlahaEnabled && this.zavlahaUnits?.length;
    this.zavlahaLegendItems?.forEach((item) => {
      item.style.display = showZavlahaLegend ? '' : 'none';
    });
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimal] = amount.split('.');
    const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formatted = formattedWhole + ',' + decimal;

    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} Kč';
    return moneyFormat
      .replace('{{amount_with_comma_separator}}', formatted)
      .replace('{{amount}}', formatted)
      .replace('{{amount_no_decimals}}', formattedWhole)
      .replace('{{amount_with_apostrophe_separator}}', formatted);
  }

  async addToCart(event) {
    event.preventDefault();

    if (!this.cartItems || this.cartItems.length === 0) {
      console.warn('PlantBox Calculator: No cart items to add. Variants:', this.variants);
      return;
    }

    console.log('PlantBox Calculator: Adding to cart:', this.cartItems);

    this.addToCartBtn.disabled = true;
    const origText = this.addToCartBtn.textContent;
    this.addToCartBtn.textContent = this.t.addingToCart;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.cartItems }),
      });

      const data = await res.json();

      if (res.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        const existingError = this.querySelector('.calculator-cart-error');
        if (existingError) existingError.style.display = 'none';
        this.addToCartBtn.textContent = this.t.addedToCart;
        setTimeout(() => {
          this.addToCartBtn.textContent = origText;
          this.addToCartBtn.disabled = false;
        }, 2000);
      } else {
        console.error('PlantBox Calculator: Cart API error:', data);
        throw new Error(data.description || data.message || 'Cart add failed');
      }
    } catch (err) {
      console.error('PlantBox Calculator: Add to cart error:', err);
      this.addToCartBtn.textContent = 'Vyprodáno';
      this.addToCartBtn.disabled = true;
      this.showCartError(err.message);
    }
  }

  showCartError(message) {
    let errorEl = this.querySelector('.calculator-cart-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'calculator-cart-error';
      errorEl.style.cssText = 'color:#cc0000;margin-top:1rem;font-size:1.4rem;';
      this.addToCartBtn.parentNode.insertBefore(errorEl, this.addToCartBtn.nextSibling);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }
}

if (!customElements.get('plantbox-calculator-section')) {
  customElements.define('plantbox-calculator-section', PlantboxCalculatorSection);
}
